import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";

const HybridCloudPage: React.FC = () => {
    return (
        <Fragment>
            <Helmet>
                <title>Hybrid Cloud Solutions | AquarLabs</title>
                <meta name="description" content="Seamlessly integrate on-premise infrastructure with cloud services. AquarLabs' hybrid cloud solutions deliver flexibility, security, and optimal performance for your enterprise." />
                <meta name="keywords" content="hybrid cloud, cloud integration, multi-cloud, on-premise cloud, enterprise cloud, cloud infrastructure, AquarLabs" />
            </Helmet>
            <div className="about-page inner-page">
                <div className="body_wrap o-clip">
                    <Header />
                    <main>
                        <PageTitle pageTitle="Hybrid Cloud Solutions" pagesub="Hybrid Cloud" parentPage="Solutions" parentLink="/service" />

                        {/* Hero Section */}
                        <section className="service-hero-section" style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.8) 100%)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <h1 style={{ fontSize: "3rem", marginBottom: "20px", color: "#fff" }}>Hybrid Cloud Solutions</h1>
                                        <p style={{ fontSize: "1.25rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.8" }}>
                                            Bridge the gap between on-premise infrastructure and cloud innovation. Our hybrid cloud solutions provide the perfect balance of security, scalability, and flexibility for modern enterprises.
                                        </p>
                                        <Link to="/contact-us" className="thm-btn agency-btn" style={{ marginTop: "30px", display: "inline-block" }}>
                                            <span className="text">Get Started</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Overview Section */}
                        <section style={{ padding: "80px 0" }}>
                            <div className="container">
                                <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Overview</span>
                                <h2 style={{ fontSize: "2.5rem", margin: "15px 0 30px", color: "#fff" }}>Unified Infrastructure for Maximum Agility</h2>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)" }}>
                                    AquarLabs' hybrid cloud solutions enable organizations to leverage the best of both worlds—combining the security and control of private infrastructure with the scalability and innovation of public cloud services. We design architectures that allow seamless data flow, consistent security policies, and unified management across all environments.
                                </p>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)", marginTop: "20px" }}>
                                    Whether you're modernizing legacy systems, meeting compliance requirements, or optimizing costs, our hybrid approach ensures your infrastructure evolves with your business needs while maintaining operational continuity.
                                </p>
                            </div>
                        </section>

                        {/* Services Section */}
                        <section style={{ padding: "80px 0", background: "rgba(0,2,15,0.3)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-4">
                                        <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase" }}>Our Capabilities</span>
                                        <h2 style={{ fontSize: "2rem", margin: "15px 0", color: "#fff" }}>Comprehensive Hybrid Cloud Services</h2>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="row">
                                            {[
                                                {
                                                    title: "Architecture Design",
                                                    desc: "Custom hybrid architectures tailored to your workload requirements.",
                                                    items: ["Multi-cloud Strategy", "Network Topology Design", "Security Framework Implementation"]
                                                },
                                                {
                                                    title: "Seamless Integration",
                                                    desc: "Connect on-premise systems with cloud platforms effortlessly.",
                                                    items: ["API Gateway Solutions", "Data Synchronization", "Identity Federation"]
                                                },
                                                {
                                                    title: "Workload Migration",
                                                    desc: "Move workloads between environments with zero disruption.",
                                                    items: ["Application Assessment", "Lift-and-Shift Migration", "Cloud-Native Refactoring"]
                                                },
                                                {
                                                    title: "Unified Management",
                                                    desc: "Single pane of glass for all your infrastructure operations.",
                                                    items: ["Centralized Monitoring", "Automated Provisioning", "Cost Optimization Tools"]
                                                }
                                            ].map((activity, index) => (
                                                <div key={index} className="col-md-6" style={{ marginBottom: "30px" }}>
                                                    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "30px", height: "100%" }}>
                                                        <h3 style={{ fontSize: "1.25rem", color: "#fff", marginBottom: "15px" }}>
                                                            <span style={{ color: "#fc9038" }}>●</span> {activity.title}
                                                        </h3>
                                                        <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.7)", marginBottom: "15px" }}>{activity.desc}</p>
                                                        <ul style={{ listStyle: "none", padding: 0 }}>
                                                            {activity.items.map((item, i) => (
                                                                <li key={i} style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", padding: "5px 0" }}>→ {item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Why Us Section */}
                        <section style={{ padding: "80px 0", background: "#0a0a0a" }}>
                            <div className="container">
                                <div className="text-center" style={{ marginBottom: "50px" }}>
                                    <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Why Us</span>
                                    <h2 style={{ fontSize: "2.5rem", margin: "15px 0 20px", color: "#fff" }}>Why Choose AquarLabs for Hybrid Cloud?</h2>
                                    <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.6)", maxWidth: "700px", margin: "0 auto" }}>
                                        Our expertise spans across major cloud platforms and enterprise infrastructure, ensuring seamless hybrid implementations.
                                    </p>
                                </div>
                                <div className="row">
                                    {[
                                        { title: "Multi-Cloud Expertise", desc: "Certified professionals across AWS, Azure, GCP, and private cloud platforms." },
                                        { title: "Enterprise Security", desc: "End-to-end security with compliance frameworks for HIPAA, SOC2, and GDPR." },
                                        { title: "24/7 Support", desc: "Round-the-clock monitoring and support for your hybrid infrastructure." },
                                        { title: "Cost Optimization", desc: "Reduce TCO with intelligent workload placement and resource optimization." },
                                        { title: "Disaster Recovery", desc: "Built-in resilience with cross-environment backup and failover capabilities." },
                                        { title: "Future-Ready", desc: "Scalable architectures designed to adapt to evolving business requirements." }
                                    ].map((item, index) => (
                                        <div key={index} className="col-lg-6" style={{ marginBottom: "20px" }}>
                                            <div style={{
                                                background: "rgba(20, 20, 20, 0.8)",
                                                border: "1px solid rgba(252, 144, 56, 0.4)",
                                                borderRadius: "16px",
                                                padding: "28px 30px",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "15px"
                                            }}>
                                                <div style={{
                                                    minWidth: "40px",
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    background: "rgba(252, 144, 56, 0.1)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="#fc9038" strokeWidth="2" strokeLinecap="round" />
                                                        <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="#fc9038" strokeWidth="2" />
                                                        <path d="M9 12L11 14L15 10" stroke="#fc9038" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 style={{ fontSize: "1.1rem", color: "#fff", marginBottom: "8px", fontWeight: "600" }}>{item.title}</h3>
                                                    <p style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", margin: 0, lineHeight: "1.6" }}>{item.desc}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.5) 100%)" }}>
                            <div className="container text-center">
                                <h2 style={{ fontSize: "2.5rem", color: "#fff", marginBottom: "20px" }}>Ready to Build Your Hybrid Cloud?</h2>
                                <p style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)", marginBottom: "30px" }}>
                                    Let's design a hybrid infrastructure that powers your digital transformation.
                                </p>
                                <Link to="/contact-us" className="thm-btn agency-btn">
                                    <span className="text">Contact Us Today</span>
                                </Link>
                            </div>
                        </section>
                    </main>
                    <Footer />
                    <Scrollbar />
                </div>
            </div>
        </Fragment>
    );
};

export default HybridCloudPage;
